import { useState } from "react";
import { toast } from "sonner";
import { UsersThree, User, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";

export default function OnboardingPage({ onDone }) {
  const { setUser } = useAuth();
  const { t } = useT();
  const [type, setType] = useState(null);
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [invite, setInvite] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces", { name, type, currency });
      setUser(data.user);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces/join", { invite_code: invite });
      setUser(data.user);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-10">
        <div>
          <p className="font-mono text-xs text-slate-400 mb-2">{t("ob_step")}</p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">{t("ob_title")}</h1>
          <p className="text-sm text-slate-500 mt-2">{t("ob_sub")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: "team", icon: UsersThree, title: t("ob_team"), desc: t("ob_team_desc") },
            { key: "freelancer", icon: User, title: t("ob_fl"), desc: t("ob_fl_desc") },
          ].map(({ key, icon: Icon, title, desc }) => (
            <button
              key={key}
              data-testid={`workspace-type-${key}`}
              onClick={() => setType(key)}
              className={`text-left border p-5 transition-[border-color,background-color] duration-150 ${type === key ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}
            >
              <Icon size={24} className="text-slate-900 mb-3" />
              <p className="font-heading font-semibold text-slate-900">{title}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
            </button>
          ))}
        </div>

        {type && (
          <div className="space-y-4 border-t border-slate-200 pt-8">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Input data-testid="workspace-name-input" placeholder={type === "team" ? t("companyName") : t("workspaceName")} value={name} onChange={(e) => setName(e.target.value)} className="rounded-none h-11" />
              </div>
              <Input data-testid="workspace-currency-input" placeholder="USD" maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className="rounded-none h-11 font-mono uppercase" />
            </div>
            <Button data-testid="create-workspace-btn" onClick={create} disabled={busy || !name.trim()} className="w-full h-11 rounded-none bg-slate-900 hover:bg-slate-700">
              {t("createWorkspace")} <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}

        <div className="border-t border-slate-200 pt-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">{t("haveInvite")}</p>
          <div className="flex gap-2">
            <Input data-testid="invite-code-input" placeholder={t("inviteCode")} value={invite} onChange={(e) => setInvite(e.target.value)} className="rounded-none h-11 font-mono" />
            <Button data-testid="join-workspace-btn" onClick={join} disabled={busy || !invite.trim()} variant="outline" className="rounded-none h-11">{t("joinTeam")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
